import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type { WireScheduleRun, WireScheduledPrompt } from '@logistics/contracts';
import { now } from '@/lib/clock';
import { expectStatus, failureOf } from '@/lib/result';
import { api } from '../client';
import { useToast } from '../shell/toast';
import { hasQueued } from './schedule-form';

export const SCHEDULES_KEY = ['schedules'] as const;
const runsKey = (id: string): readonly string[] => ['schedules', 'runs', id];

export interface ScheduleList {
  readonly schedules: WireScheduledPrompt[];
  readonly defaultTimezone: string;
  readonly timezones: string[];
}

/** While a run is queued, how often to look again; the bot picks it up within a tick. */
const QUEUED_POLL_MS = 5_000;

/**
 * Every schedule, with the zones a form offers. Admin only. Polls while a run
 * is queued, so "Last" turns from "never" to "ok" without a reload.
 */
export function useSchedules(): UseQueryResult<ScheduleList> {
  return useQuery({
    queryKey: SCHEDULES_KEY,
    queryFn: async () => expectStatus(await api.schedules.list(), 200),
    refetchInterval: (query) =>
      hasQueued(query.state.data?.schedules ?? [], now().getTime()) ? QUEUED_POLL_MS : false,
  });
}

/** One schedule's recent runs, newest first. */
export function useScheduleRuns(id: string): UseQueryResult<WireScheduleRun[]> {
  return useQuery({
    queryKey: runsKey(id),
    queryFn: async () => expectStatus(await api.schedules.get({ params: { id } }), 200).runs,
  });
}

interface Action {
  readonly schedule: WireScheduledPrompt;
  readonly kind: 'toggle' | 'run' | 'remove';
}

const DONE: Readonly<Record<Action['kind'], (s: WireScheduledPrompt) => string>> = {
  toggle: (s) => (s.enabled ? `${s.name} paused` : `${s.name} resumed`),
  run: (s) => `${s.name} is queued; the answer arrives within a minute or two`,
  remove: (s) => `${s.name} deleted`,
};

/** Pause or resume, run now, or delete — each ending in a toast. */
export function useScheduleAction(): UseMutationResult<void, Error, Action> {
  const toast = useToast();
  const client = useQueryClient();
  return useMutation<void, Error, Action>({
    mutationFn: async ({ schedule, kind }) => {
      const params = { id: schedule.id };
      const r =
        kind === 'toggle'
          ? await api.schedules.toggle({ params, body: {} })
          : kind === 'run'
            ? await api.schedules.run({ params, body: {} })
            : await api.schedules.remove({ params, body: {} });
      if (r.status !== 200 && r.status !== 202) {
        toast(failureOf(r).detail ?? `Could not change ${schedule.name} (${r.status})`);
        return;
      }
      toast(DONE[kind](schedule));
      await client.invalidateQueries({ queryKey: SCHEDULES_KEY });
    },
  });
}
